import { DateType } from "@date-io/type";
import { TextField, TextFieldProps } from "@mui/material";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import { useEffect } from "react";
import { Controller, FieldValues, Path, useFormContext, Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type FormDatePickerProps<IFormValues extends FieldValues = FieldValues> = {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    shouldUnregister?: boolean,
    label?: string,
    textFieldProps?: Omit<TextFieldProps, 'name' | 'value' | 'label' | 'error'>
} & Omit<DatePickerProps<string, DateType>, 'renderInput' | 'value' | 'ref' | 'label' | 'onChange'> & {
    onChange?: DatePickerProps<string, DateType>['onChange']
}

const FormDatePicker = <IFormValues extends FieldValues = FieldValues>({
    name,
    control,
    shouldUnregister,
    label,
    textFieldProps,
    ...props
}: FormDatePickerProps<IFormValues>) => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    useEffect(() => {
        return () => {
            const innerControl = control ?? formContext.control;
            const currentValue: Dayjs | null = _.get(innerControl._formValues, name);
            if (currentValue && !dayjs(currentValue).isValid()) {
                _.set(innerControl._formValues, name, null);
            }
        }
    }, []);

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            shouldUnregister={shouldUnregister}
            render={({ field, fieldState, formState }) =>
                <DatePicker
                    {...props}
                    ref={field.ref}
                    value={field.value}
                    onChange={(value) => {
                        field.onChange(value);
                        props?.onChange?.(value);
                    }}
                    label={label}
                    inputFormat={props?.inputFormat ?? 'L'}
                    disabled={props.disabled ?? formState.isSubmitting}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...textFieldProps}
                            onBlur={field.onBlur}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message ? t(fieldState.error.message) : textFieldProps?.helperText}
                            fullWidth
                        />
                    }
                />
            }
        />
    )
};

export default FormDatePicker;